import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";

import { useRegisterMutation } from "@/api/auth";
import { ApiError } from "@/api/client";
import { BrandMark, BrandName, Button, Ribbon } from "@/components/ui";
import { t } from "@/i18n";

/** /kayit/:tenantSlug — sozlesme §2 "/kayit (tenant slug)". */
export function Kayit() {
  const { tenantSlug: routeSlug } = useParams<{ tenantSlug?: string }>();
  const registerMutation = useRegisterMutation();

  const [tenantSlug, setTenantSlug] = useState(routeSlug ?? "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    // Eski legacy/js/register.js ile ayni istemci-tarafi kontrol — gonderim
    // ONCESI, sunucuya hic istek atmadan durdurur.
    if (password !== passwordConfirm) {
      setError(t("auth.register.password_mismatch"));
      return;
    }
    try {
      await registerMutation.mutateAsync({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        tenant_slug: tenantSlug,
      });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    }
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-pale to-bg px-4 py-8">
        <div className="w-full max-w-[420px] space-y-4 rounded-2xl border-[1.5px] border-line bg-card p-7 text-center shadow-login sm:p-9">
          <div className="mb-2 flex flex-col items-center">
            <BrandMark size={52} className="mb-3" />
            <BrandName className="text-[22px]" />
          </div>
          <p className="text-sm text-ink">{t("auth.register.success")}</p>
          <Link to="/giris" className="btn-link !min-h-[36px]">
            {t("auth.login.title")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-pale to-bg px-4 py-8">
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="w-full max-w-[420px] space-y-4 rounded-2xl border-[1.5px] border-line bg-card p-7 shadow-login sm:p-9"
      >
        <div className="mb-2 flex flex-col items-center text-center">
          <BrandMark size={52} className="mb-3" />
          <BrandName className="text-[22px]" />
        </div>

        <h1 className="font-display text-lg font-bold text-ink">{t("auth.register.title")}</h1>

        <Field id="tenantSlug" label={t("auth.register.tenant_slug")} value={tenantSlug} onChange={setTenantSlug} />
        <Field id="firstName" label={t("auth.register.first_name")} value={firstName} onChange={setFirstName} required={false} />
        <Field id="lastName" label={t("auth.register.last_name")} value={lastName} onChange={setLastName} required={false} />
        <Field id="email" label={t("auth.login.email")} type="email" value={email} onChange={setEmail} autoComplete="email" />
        <div className="space-y-1">
          <Field
            id="password"
            label={t("auth.login.password")}
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            minLength={8}
          />
          <p className="text-xs text-muted">{t("auth.register.password_hint")}</p>
        </div>

        <Field
          id="passwordConfirm"
          label={t("auth.register.password_confirm")}
          type="password"
          value={passwordConfirm}
          onChange={setPasswordConfirm}
          autoComplete="new-password"
          minLength={8}
        />

        {error && <Ribbon tone="bad">{error}</Ribbon>}

        <Button type="submit" variant="primary" disabled={registerMutation.isPending} className="w-full !min-h-[46px] text-[15px]">
          {t("auth.register.submit")}
        </Button>

        <Link to="/giris" className="block text-center text-sm font-medium text-brand-primary hover:underline">
          {t("auth.login.title")}
        </Link>
      </form>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  minLength?: number;
}

function Field({ id, label, value, onChange, type = "text", required = true, autoComplete, minLength }: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className="label">
        {label}
      </label>
      <input
        id={id}
        type={type}
        required={required}
        autoComplete={autoComplete}
        minLength={minLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input"
      />
    </div>
  );
}
